import { Box, styled } from '@mui/material';
import { useEffect } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { FloatingStripButton } from 'src/components/FloatingStrip/floating-strip';
import {
  BulkUpdateEntitiesProgress,
  isProgressTerminal,
  useBulkUpdateEntitiesProgressQuery,
} from 'src/graphql/entities/custom-view/custom-view-bulk-update';
import {
  getBulkUpdateBannerTitle,
  getBulkUpdateProgressDetails,
} from 'src/pages/CustomViews/components/bulk-update-banner.helpers';
import {
  BANNER_ICON_SIZE,
  BulkJobProgress,
  BulkJobStatusIcon,
  BulkJobStrip,
  BulkJobText,
} from 'src/pages/CustomViews/components/bulk-update-job-banner.elements';
import {
  getBulkUpdateViewContextKey,
  isBulkUpdateJobInPartnerScope,
  useBulkUpdateJobsStore,
  useGlobalStore,
} from 'src/store';
import { AppEvents, emitAppEvent } from 'src/utils/hooks/use-app-events';

// Outside its source view the job has no list to sit on, so it keeps the window's centre.
const GlobalBannerPosition = styled(Box, { name: 'BulkUpdateGlobalBannerPosition' })(
  ({ theme }) => ({
    position: 'fixed',
    left: '50%',
    bottom: '0.75rem',
    transform: 'translateX(-50%)',
    zIndex: theme.zIndex.appBar,
    width: 'max-content',
    maxWidth: 'calc(100vw - 1.5rem)',
  }),
);

export const BulkUpdateGlobalBanner = () => {
  const activeBulkUpdateJob = useBulkUpdateJobsStore.use.activeBulkUpdateJob();
  const sourceViewContextKey = useBulkUpdateJobsStore.use.bulkUpdateSourceViewContextKey();
  const dismissBulkUpdateJob = useBulkUpdateJobsStore.use.dismissBulkUpdateJob();
  const updateBulkUpdateJob = useBulkUpdateJobsStore.use.updateBulkUpdateJob();
  const markBulkUpdateViewForRefresh =
    useBulkUpdateJobsStore.use.markBulkUpdateViewForRefresh();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const contextKey = activeBulkUpdateJob
    ? getBulkUpdateViewContextKey(activeBulkUpdateJob.entityName, activeBulkUpdateJob.viewId)
    : null;

  const isJobInCurrentScope = isBulkUpdateJobInPartnerScope(
    activeBulkUpdateJob,
    selectedPartnerId,
  );

  const { data: bulkUpdateProgressData, loading: isBulkUpdateProgressLoading } =
    useBulkUpdateEntitiesProgressQuery(
      isJobInCurrentScope ? activeBulkUpdateJob?.jobId : null,
      2_000,
    );

  const bulkUpdateProgress = (
    bulkUpdateProgressData as
      | {
          bulkUpdateEntitiesProgress?: BulkUpdateEntitiesProgress;
        }
      | undefined
  )?.bulkUpdateEntitiesProgress;

  const normalizedStatus = bulkUpdateProgress?.status?.toLowerCase();
  const isTerminal = isProgressTerminal(normalizedStatus);
  const isSourceViewActive = Boolean(contextKey && sourceViewContextKey === contextKey);

  useEffect(() => {
    if (
      !activeBulkUpdateJob ||
      !bulkUpdateProgress?.status ||
      bulkUpdateProgress.status === activeBulkUpdateJob.status
    ) {
      return;
    }
    updateBulkUpdateJob(activeBulkUpdateJob.jobId, { status: bulkUpdateProgress.status });
  }, [activeBulkUpdateJob, bulkUpdateProgress?.status, updateBulkUpdateJob]);

  useEffect(() => {
    if (
      !activeBulkUpdateJob ||
      !activeBulkUpdateJob.isUndoInProgress ||
      !normalizedStatus ||
      (normalizedStatus !== 'undone' && normalizedStatus !== 'failed')
    ) {
      return;
    }
    updateBulkUpdateJob(activeBulkUpdateJob.jobId, { isUndoInProgress: false });
  }, [activeBulkUpdateJob, normalizedStatus, updateBulkUpdateJob]);

  useEffect(() => {
    if (
      !activeBulkUpdateJob ||
      !contextKey ||
      !isTerminal ||
      activeBulkUpdateJob.hasTerminalSyncApplied
    ) {
      return;
    }

    emitAppEvent(AppEvents.SearchRecordsInvalidated, {
      entityName: activeBulkUpdateJob.entityName,
      viewId: activeBulkUpdateJob.viewId,
    });
    markBulkUpdateViewForRefresh(contextKey);
    updateBulkUpdateJob(activeBulkUpdateJob.jobId, {
      hasTerminalSyncApplied: true,
    });
  }, [
    activeBulkUpdateJob,
    contextKey,
    isTerminal,
    markBulkUpdateViewForRefresh,
    updateBulkUpdateJob,
  ]);

  if (
    !activeBulkUpdateJob ||
    activeBulkUpdateJob.dismissed ||
    isSourceViewActive ||
    !isJobInCurrentScope ||
    (!isBulkUpdateProgressLoading && !bulkUpdateProgress)
  ) {
    return null;
  }

  const title = getBulkUpdateBannerTitle({
    operationKind: activeBulkUpdateJob.operationKind,
    status: normalizedStatus ?? undefined,
    isUndoInProgress: activeBulkUpdateJob.isUndoInProgress,
  });
  const details = getBulkUpdateProgressDetails({
    operationKind: activeBulkUpdateJob.operationKind,
    progress: bulkUpdateProgress,
    separator: ' - ',
  });
  const progressPercent =
    bulkUpdateProgress && bulkUpdateProgress.total > 0
      ? Math.max(
          0,
          Math.min(
            100,
            Math.round((bulkUpdateProgress.processed / bulkUpdateProgress.total) * 100),
          ),
        )
      : 0;

  return (
    <GlobalBannerPosition>
      <BulkJobStrip tone="raised" data-testid="bulk-update-global-banner">
        <BulkJobStatusIcon
          status={normalizedStatus}
          isUndoInProgress={activeBulkUpdateJob.isUndoInProgress}
        />
        <BulkJobText>
          <b>{title}</b>
          <span>{details}</span>
        </BulkJobText>
        <FloatingStripButton
          iconOnly
          onClick={() => dismissBulkUpdateJob(activeBulkUpdateJob.jobId)}
          aria-label="Dismiss bulk update status banner"
          startIcon={<DynamicIcon name="x" library="lucide" size={BANNER_ICON_SIZE} />}
        />
        {!isTerminal || isBulkUpdateProgressLoading ? (
          <BulkJobProgress
            variant={bulkUpdateProgress?.total ? 'determinate' : 'indeterminate'}
            value={bulkUpdateProgress?.total ? progressPercent : undefined}
          />
        ) : null}
      </BulkJobStrip>
    </GlobalBannerPosition>
  );
};
